import * as React from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import { ColumnDef, DataTable } from "../../components/Table";
import type { UserInfo, UserStatus } from "../../types";
import { COLORS } from "../../theme/theme-options";
import DialogContext from "../../contexts/DialogContext";
import { useUsers } from "../../hooks/query/useUsers";
import { useCurrentUser } from "../../hooks/query/useCurrentUser";
import RowActionsMenu from "../../components/RowActionsMenu";

const chipSxByStatus: Record<UserStatus, Record<string, string | number>> = {
  pending: {
    color: "#fbbf24",
    backgroundColor: alpha("#f59e0b", 0.12),
    border: `1px solid ${alpha("#f59e0b", 0.32)}`,
  },
  confirmed: {
    color: "#34d399",
    backgroundColor: alpha("#10b981", 0.12),
    border: `1px solid ${alpha("#10b981", 0.32)}`,
  },
};

const roleChipSx = {
  color: COLORS.primary[200],
  backgroundColor: alpha(COLORS.primary[500], 0.16),
  border: `1px solid ${alpha(COLORS.primary[400], 0.3)}`,
};

const columns: ColumnDef<UserInfo>[] = [
  {
    id: "email",
    header: "Email",
    render: (row) => row.email,
  },
  {
    id: "firstName",
    header: "First Name",
    render: (row) => row.firstName,
  },
  {
    id: "lastName",
    header: "Last Name",
    render: (row) => row.lastName,
  },
  {
    id: "role",
    header: "Role",
    render: (row) => {
      return <Chip size="small" label={row.role} sx={roleChipSx} />;
    },
  },
  {
    id: "status",
    header: "Status",
    render: (row) => {
      const status = row.status;
      return (
        <Chip
          size="small"
          label={status === "confirmed" ? "Confirmed" : "Pending"}
          sx={chipSxByStatus[status]}
        />
      );
    },
  },
];

const UsersConfigPage: React.FC = () => {
  const { data: users = [], isLoading } = useUsers();
  const { data: currentUser } = useCurrentUser();
  const { show } = React.useContext(DialogContext);
  const [rowMenu, setRowMenu] = React.useState<{
    anchorEl: HTMLElement;
    user: UserInfo;
  } | null>(null);
  const isCurrentUserSelected =
    rowMenu && currentUser
      ? rowMenu.user.id === currentUser.id
      : false;

  const rows = React.useMemo(
    () => users,
    [users],
  );

  return (
    <Box display="flex" flexDirection="column" gap={2.2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack spacing={0.4}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: COLORS.grey[100] }}>
            Users
          </Typography>
          <Typography sx={{ color: COLORS.grey[500] }}>
            Manage and invite users to your workspace
          </Typography>
        </Stack>
        <Button
          variant="outlined"
          startIcon={<PersonAddRoundedIcon fontSize="small" />}
          onClick={() => {
            show({ dialog: "invite-user" });
          }}
        >
          Invite
        </Button>
      </Stack>

      <DataTable
        title="Users"
        loading={isLoading}
        rows={rows}
        columns={columns}
        colSpan={6}
        selectable={false}
        onRowMenuClick={(user, anchorEl) => {
          setRowMenu({ user, anchorEl });
        }}
      />

      <RowActionsMenu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
        onEdit={() => {
          const userId = rowMenu?.user.id;
          if (!userId) {
            return;
          }
          show({ dialog: "edit-user", userId });
        }}
        onDelete={() => {
          const userId = rowMenu?.user.id;
          if (!userId) {
            return;
          }
          show({
            dialog: "confirm-delete",
            entity: "user",
            ids: userId,
          });
        }}
        disableDelete={isCurrentUserSelected}
        disableDeleteReason="Current authenticated user cannot be deleted."
      />
    </Box>
  );
};

export default UsersConfigPage;
